import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, updateProfile } from 'firebase/auth';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import defaultAvatar from '../assets/svg/images/default-avatar.jpg';
import { FaCamera } from 'react-icons/fa';

export default function AddProfileImage() {
  const [previewImage, setPreviewImage] = useState(defaultAvatar);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const auth = getAuth();

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('Image size should not exceed 10MB');
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const uploadImage = async () => {
    if (!selectedFile) return defaultAvatar;
    
    const storage = getStorage();
    const user = auth.currentUser;
    const storageRef = ref(storage, `profileImages/${user.uid}`);
    
    try {
      setIsUploading(true);
      const snapshot = await uploadBytes(storageRef, selectedFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      toast.error('Error uploading image');
      return defaultAvatar;
    } finally {
      setIsUploading(false);
    }
  };

  const handleFinish = async () => {
    try {
      const user = auth.currentUser;
      const photoURL = await uploadImage();
      
      await updateProfile(user, { photoURL });
      await updateDoc(doc(db, 'users', user.uid), {
        photoURL: photoURL
      });
      
      toast.success('Profile setup completed!');
      navigate('/');
    } catch (error) {
      toast.error('Upload image to Finish or Skip');
    }
  };

  const handleSkip = () => {
    // Simply navigate to home without updating profile
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-transparent">
      <div className="flex flex-col items-center space-y-8">
        {/* Profile Image Preview */}
        <div className="relative">
          <img
            src={previewImage}
            alt="Profile preview"
            className="w-48 h-48 md:w-64 md:h-64 rounded-full object-cover border-4 border-gray-800 shadow-lg"
          />
          <button
            onClick={handleUploadClick}
            className="absolute bottom-2 right-2 bg-gray-900 p-3 rounded-full hover:bg-gray-800 transition-colors duration-200"
            disabled={isUploading}
          >
            <FaCamera className="text-white text-xl" />
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageChange}
            accept="image/*"
            capture="environment"
            className="hidden"
          />
        </div>

        {/* Buttons */}
        <div className="flex space-x-6">
          <button
            onClick={handleSkip}
            className="px-8 py-3 bg-black bg-opacity-70 text-white rounded-full text-sm font-semibold hover:bg-opacity-90 transition-all duration-200"
            disabled={isUploading}
          >
            Skip
          </button>
          <button
            onClick={handleFinish}
            className="px-8 py-3 text-white text-sm font-semibold rounded-full shadow-lg transition duration-150 ease-in-out hover:shadow-xl bg-gradient-to-r from-[#FF1493] via-[#FF8C00] to-[#FFD700] animate-gradient-x disabled:opacity-50"
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Finish'}
          </button>
        </div>
      </div>

      {/* Global Styles */}
      <style jsx global>{`
        @keyframes gradient-x {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
      `}</style>
    </div>
  );
}